import { useCallback, useEffect, useRef, useState } from 'react';

import {
  FormControl,
  FormHelperText,
  Input,
  InputAdornment,
  InputLabel,
  Popover,
} from '@mui/material';

import { useEllipsis } from '@/hooks/useEllipsis';

import { Carousel } from '@/components/Carousel';
import { SimpleError } from '@/components/formik/error/SimpleError';
import { ChevronDown } from '@/components/icons/ChevronDown';

import { MultiSelectSearch } from './MultiSelectSearch';
import { MultiSelectSelected } from './MultiSelectSelected';
import { styles } from './MutliSelect.styles';
import type { TCache, TMultiSelectProps, TSelectItem } from './types';

const toDisplay = ({ title }: TSelectItem) => title;

export const MultiSelect = ({
  value: extValue,
  onChange,
  error,
  label,
  getOptions,
  placeholder,
  ...controlProps
}: TMultiSelectProps) => {
  const cache = useRef<TCache>({});
  const fieldRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [value, setValue] = useState(extValue);

  const summary = useEllipsis(inputRef, value, toDisplay);

  const getOptionsCached = useCallback(
    async (query: string, signal: AbortSignal) => {
      if (!cache.current[query]) {
        cache.current[query] = await getOptions(query, signal);
      }

      return cache.current[query];
    },
    [getOptions],
  );

  const openMenu = () => {
    setMenuOpen(true);
  };

  const toggle = (items: TSelectItem[]) => {
    const result = [...value];

    items.forEach((item) => {
      const index = result.findIndex(({ id }) => id === item.id);

      if (index !== -1) {
        result.splice(index, 1);
      } else {
        result.push(item);
      }
    });

    setValue(result);
    onChange(result);
  };

  const props = { ...controlProps, value, toggle, getOptions: getOptionsCached };

  useEffect(() => {
    setValue(extValue);
  }, [extValue]);

  return (
    <>
      <FormControl onClick={openMenu} focused={menuOpen}>
        <InputLabel shrink={value.length > 0 || menuOpen}>{label}</InputLabel>

        <Input
          ref={fieldRef}
          value={summary}
          onFocus={openMenu}
          placeholder={placeholder}
          inputRef={inputRef}
          endAdornment={
            <InputAdornment position="end">
              <ChevronDown />
            </InputAdornment>
          }
          readOnly
        />

        <FormHelperText component="div">
          <SimpleError error={error} errorsShown={!!error} label={label} />
        </FormHelperText>
      </FormControl>

      <Popover
        anchorEl={fieldRef.current}
        open={menuOpen && !!fieldRef.current}
        onClose={() => setMenuOpen(false)}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        PaperProps={{
          sx: [styles.dropDown, { width: fieldRef.current?.clientWidth }],
        }}
        disableRestoreFocus
      >
        <Carousel panes={['search', 'selected']} sx={styles.pane}>
          {(pane, activate) => {
            switch (pane) {
              case 'search':
                return <MultiSelectSearch {...props} showSelected={() => activate('selected')} />;

              case 'selected':
                return <MultiSelectSelected {...props} backToSearch={() => activate('search')} />;
            }
          }}
        </Carousel>
      </Popover>
    </>
  );
};
